// Manual quotation workbench: one supplier, all unquoted items of a category.
// Per item: the full restaurant request (left) vs. the supplier match (right)
// with suggestions → full-catalog search → inline create, then the price
// ladder (Public → Deal → Negotiated, prefilled deal ?? public). Saving POSTs
// one quote per selection to POST :id/items/:itemId/quotes (409 = duplicate,
// tolerated); a ticked "Save to Price tool" line sends syncSupplierPrice: true,
// and the BE upserts supplier_prices in the same transaction. Replaces the old
// AddSupplierQuoteModal, which was limited to deal-backed products and the
// sparse mirror item shape.

import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  ChakraAsyncSelect,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Input,
  Text,
  useToast,
} from '@elkaso-app/web-design';
import type {
  ExclusiveDealSummary,
  QuotationSupplierProduct,
  RestaurantInquiryItem,
  RestaurantInquiryQuote,
} from 'apis/restaurant-inquiries';
import {
  QuotationSupplierOption,
  restaurantInquiriesEndpoint,
  RestaurantInquiryItemStatus,
  useCreateInquiryItemQuoteApi,
  useCreateQuotationProductFromMasterApi,
  useSearchQuotationSuppliersApi,
} from 'apis/restaurant-inquiries';
import { EditItemModal } from 'pages/inquiry-processor/components/edit-item-modal';
import { ProductQuoteHistoryModal } from 'pages/inquiry-processor/components/product-quote-history';
import { EditMasterProductModal } from 'pages/inquiry-processor/components/quote-matcher/edit-master-product-modal';
import { InlineAddDealPopover } from 'pages/inquiry-processor/components/quote-matcher/inline-add-deal-popover';
import type { StagedVariant } from 'pages/inquiry-processor/components/quote-matcher/product-matcher';
import { ProductMatcher } from 'pages/inquiry-processor/components/quote-matcher/product-matcher';
import { QuoteLinePricing } from 'pages/inquiry-processor/components/quote-matcher/quote-line-pricing';
import { RequestedProductPanel } from 'pages/inquiry-processor/components/quote-matcher/requested-product-panel';
import { SupplierTermsStrip } from 'pages/inquiry-processor/components/quote-matcher/supplier-terms-strip';
import { readSyncPreference, writeSyncPreference } from 'pages/inquiry-processor/utils/price-sync-preference';
import { ladderOf, resolveTier, type PriceTier, type UomLadder } from 'pages/inquiry-processor/utils/price-units';
import { useMemo, useState } from 'react';
import { FiChevronDown, FiClock } from 'react-icons/fi';
import { useQueryClient } from 'react-query';

// The supplier select's option, minus the profile payload — the matrix's
// "+ Quote another SKU" preselect only knows the column's id+name. Without
// `raw` the terms strip and inline add-deal stay hidden until the operator
// re-picks the supplier from the dropdown.
type SupplierPick = Pick<QuotationSupplierOption, 'value' | 'label'> & { raw?: QuotationSupplierOption['raw'] };

interface Props {
  isOpen: boolean;
  onClose: () => void;
  inquiryId: string;
  category: string;
  items: RestaurantInquiryItem[]; // the category's items (with quotes[])
  // Preselects the supplier (multi-alternative flow from a price cell).
  initialSupplier?: SupplierPick;
}

// product is null while pendingCreate: the supplier product is only inserted
// (from the item's master product) when the operator saves — a misclick on
// "Yes — quote it" never pollutes the catalog (spec: master-linked-supplier-products).
type Selection = Record<
  number,
  {
    product: QuotationSupplierProduct | null;
    pendingCreate?: boolean;
    // Similar-variant staged create: the sibling master variant the supplier
    // actually carries (undefined = the item's exact master product).
    masterProductId?: number;
    variantLabel?: string;
    // The staged variant's own packaging ladder (rung choices for the line).
    ladder?: UomLadder;
    price: string;
    // The operator's unit pick for this line; undefined until they choose one,
    // so the effective rung re-resolves from the product's ladder.
    priceUnit?: PriceTier;
    // Tick = also save the price (and rung) to supplier_prices as the
    // supplier's standing price. Rides the POST as syncSupplierPrice; the BE
    // does the upsert in the quote's transaction. Starts from the sticky
    // preference.
    sync: boolean;
    // Set once the operator types — the supplier-price prefill lands async and
    // must never overwrite an edit.
    priceTouched?: boolean;
  }
>;

export const QuoteWorkbenchDrawer = ({ isOpen, onClose, inquiryId, category, items, initialSupplier }: Props) => {
  const toast = useToast();
  const { loadOptions } = useSearchQuotationSuppliersApi();
  const createQuote = useCreateInquiryItemQuoteApi();
  const createFromMaster = useCreateQuotationProductFromMasterApi();

  const queryClient = useQueryClient();
  const [supplierOpt, setSupplierOpt] = useState<SupplierPick | null>(initialSupplier ?? null);
  const [notes, setNotes] = useState('');
  const [sel, setSel] = useState<Selection>({});
  // Accordion state per item id. Collapsing is MANUAL only (header click /
  // Collapse all) — picking a product keeps the card open so the operator
  // sees the prefilled negotiated price and can adjust it before moving on
  // (product-owner decision, v1.2). The header chip mirrors the selection
  // either way.
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});
  // Inline data-fix loop targets (null = modal closed).
  const [editItem, setEditItem] = useState<RestaurantInquiryItem | null>(null);
  const [editMaster, setEditMaster] = useState<RestaurantInquiryItem | null>(null);

  const toggleCollapsed = (itemId: number) => setCollapsed((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  const setAllCollapsed = (value: boolean) => setCollapsed(Object.fromEntries(items.map((it) => [it.id, value])));

  // Terminal items are excluded — awarded/closed items take no new quotes (the
  // create endpoint 400s). Already-quoted items STAY visible: a supplier may
  // offer several alternative SKUs on one item (uniqueness is per item+product),
  // so re-opening the workbench adds alternatives; edits happen on the matrix.
  const visibleItems = useMemo(
    () =>
      items.filter(
        (it) =>
          it.status !== RestaurantInquiryItemStatus.FULFILLED && it.status !== RestaurantInquiryItemStatus.UNFULFILLED
      ),
    [items]
  );

  // The picked supplier's live quotes per item — the "already quoted" context
  // line, and the SKUs the matcher must refuse to re-pick (per-product 409).
  // Memoized so the ids array identity survives price-typing re-renders.
  const quotedByItem = useMemo(() => {
    const m = new Map<number, { quotes: RestaurantInquiryQuote[]; ids: number[] }>();
    if (!supplierOpt) return m;
    visibleItems.forEach((it) => {
      const quotes = (it.quotes || []).filter((q) => q.supplierId === supplierOpt.value && q.status !== 'REJECTED');
      if (quotes.length) m.set(it.id, { quotes, ids: quotes.map((q) => q.productId) });
    });
    return m;
  }, [visibleItems, supplierOpt]);

  const pick = (itemId: number, product: QuotationSupplierProduct | null) => {
    setSel((prev) => {
      const next = { ...prev };
      if (!product) {
        delete next[itemId];
        return next;
      }
      // Prefill from the deal (fallback: public price) — editable, and the
      // inactive-deal case is already flagged on the product row. The line's
      // pricing panel refines this from supplier_prices once it resolves.
      const suggested = product.exclusiveDeals?.[0]?.dealPrice ?? product.defaultPrice;
      next[itemId] = { product, price: suggested != null ? String(suggested) : '', sync: readSyncPreference() };
      return next;
    });
  };

  // "Does this supplier carry it?" answered yes — stage the deferred create.
  // variant = they carry a SIBLING variant of the master family, not the exact one.
  const stageCreate = (itemId: number, variant?: StagedVariant) =>
    setSel((prev) => ({
      ...prev,
      [itemId]: {
        product: null,
        pendingCreate: true,
        masterProductId: variant?.id,
        variantLabel: variant?.label,
        ladder: variant?.ladder,
        price: '',
        sync: readSyncPreference(),
      },
    }));

  const setPrice = (itemId: number, price: string) =>
    setSel((prev) => (prev[itemId] ? { ...prev, [itemId]: { ...prev[itemId], price, priceTouched: true } } : prev));

  const setPriceUnit = (itemId: number, priceUnit: PriceTier) =>
    setSel((prev) => (prev[itemId] ? { ...prev, [itemId]: { ...prev[itemId], priceUnit } } : prev));

  const setSync = (itemId: number, sync: boolean) =>
    setSel((prev) => (prev[itemId] ? { ...prev, [itemId]: { ...prev[itemId], sync } } : prev));

  // The supplier's standing price, once the matrix resolves. Applied only
  // while the operator hasn't typed — a late fetch must not clobber an edit.
  // The rung comes along with it: the price means nothing without its rung.
  const applySupplierPrice = (itemId: number, price: string, tier?: PriceTier) =>
    setSel((prev) => {
      const current = prev[itemId];
      if (!current || current.priceTouched) return prev;
      return { ...prev, [itemId]: { ...current, price, priceUnit: tier ?? current.priceUnit } };
    });

  // Rungs a line's price can be agreed against: the chosen catalog product's
  // ladder, else the staged sibling variant's own ladder, else the item's
  // master (the "yes — quote it" path copies exactly that).
  const ladderFor = (it: RestaurantInquiryItem, s: Selection[number]): UomLadder =>
    s.product ? ladderOf(s.product) : s.ladder ?? it.masterProduct ?? {};

  // The rung a line's price is agreed against. A staged line now carries one
  // too (it used to be left for the backend to derive), because the operator
  // picks it explicitly and a ticked line writes that same rung to supplier_prices.
  const unitFor = (it: RestaurantInquiryItem, s: Selection[number]) =>
    resolveTier(ladderFor(it, s), s.priceUnit, s.product?.defaultUnit);

  // The master product a line quotes — the staged sibling variant, else the
  // linked catalog product's master, else the item's own. Also the key the
  // supplier-price lookup and the Price-tool write-back are made against; null
  // means neither is possible (an unlinked catalog product).
  const masterIdFor = (it: RestaurantInquiryItem, s: Selection[number]): number | undefined =>
    s.masterProductId ?? s.product?.masterProductId ?? (s.product ? undefined : it.masterProductId);

  // A deal created inline (PriceLadder "+ Add deal") lands on the product copy
  // held in the selection — the catalog caches are invalidated by the popover,
  // but the ladder must flip to DEAL without waiting for a refetch.
  const applyDealToSelection = (itemId: number, deal: ExclusiveDealSummary) =>
    setSel((prev) => {
      const current = prev[itemId];
      if (!current?.product) return prev;
      const product = { ...current.product, exclusiveDeals: [deal, ...(current.product.exclusiveDeals ?? [])] };
      return { ...prev, [itemId]: { ...current, product } };
    });

  // Ships with the BE pair of this feature; absent on older BEs, which simply
  // hides the inline add-deal affordance (the deal create endpoint requires it).
  const supplierCountryCode = supplierOpt?.raw?.supplier?.countryCode || '';

  // Legacy localStorage-only inquiries have non-numeric ids — the history
  // endpoint 400s on those, so omit the param.
  const excludeInquiryId = /^\d+$/.test(inquiryId) ? Number(inquiryId) : undefined;
  const lines = visibleItems.map((it) => ({ it, s: sel[it.id] })).filter((l) => !!l.s);

  const save = async () => {
    if (!supplierOpt) {
      toast({ title: 'Please select a supplier', status: 'warning' });
      return;
    }
    if (!lines.length) {
      toast({ title: 'Pick at least one product to quote', status: 'warning' });
      return;
    }
    if (lines.some(({ s }) => !(Number(s.price) > 0))) {
      toast({ title: 'Every quoted item needs a negotiated price greater than 0', status: 'warning' });
      return;
    }
    let created = 0;
    let duplicates = 0;
    let synced = 0;
    let offered = 0; // lines that actually showed the checkbox
    for (const { it, s } of lines) {
      const lineLabel = s.product?.nameEn || it.masterProduct?.name || `Item #${it.id}`;
      const tier = unitFor(it, s) ?? undefined;
      // The checkbox is hidden on a line with no master product — there is
      // nothing to key the upsert by — so the sticky preference must never
      // leak out as a flag the operator was never offered: the BE 400s it,
      // and that would fail the whole quote, not just the write-back.
      const canSync = masterIdFor(it, s) != null;
      const sync = s.sync && canSync;
      if (canSync) offered++;
      try {
        // Deferred create: the "yes, this supplier carries it" answer only
        // materializes as a catalog row now, at save time. Idempotent server-
        // side (an existing linked product is returned, never duplicated).
        let productId = s.product?.id;
        if (!productId && s.pendingCreate) {
          const res = await createFromMaster.mutateAsync({
            supplierId: supplierOpt.value,
            itemId: it.id,
            masterProductId: s.masterProductId,
          });
          productId = res?.data?.id;
        }
        if (!productId) continue; // defensive: nothing to quote
        await createQuote.mutateAsync({
          id: inquiryId,
          itemId: it.id,
          productId,
          price: Number(s.price),
          currency: 'AED',
          priceUnit: tier,
          notes: notes.trim() || undefined,
          syncSupplierPrice: sync,
        });
        created++;
        if (sync) synced++;
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 409) {
          duplicates++; // this product is already quoted on that item — fine
        } else {
          const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
          toast({
            title: `Failed to quote “${lineLabel}”`,
            description: String(message || 'Please try again.'),
            status: 'error',
          });
          return; // keep the drawer open so nothing silently half-saves
        }
      }
    }
    if (created || duplicates) {
      toast({
        title: `${created} quote${created === 1 ? '' : 's'} created${
          duplicates ? ` · ${duplicates} already existed` : ''
        }${synced ? ` · ${synced} saved to the Price tool` : ''}`,
        status: created ? 'success' : 'info',
      });
    }
    // Only a save that actually offered the choice may move the preference —
    // an all-unlinked batch says nothing about what the operator wants.
    if (created && offered) writeSyncPreference(synced > 0);
    setSel({});
    onClose();
  };

  return (
    <>
      <Drawer isOpen={isOpen} onClose={onClose} placement='right' size='xl'>
        <DrawerOverlay bg='rgba(15,22,38,.55)' />
        <DrawerContent maxW={{ base: '100vw', md: '72vw' }}>
          <DrawerCloseButton />
          <DrawerHeader borderBottom='1px solid' borderColor='gray.100'>
            Add supplier quotes — {category}
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap='4' mt='3'>
              <FormControl isRequired>
                <FormLabel fontSize='sm'>Supplier</FormLabel>
                <ChakraAsyncSelect
                  loadOptions={loadOptions}
                  defaultOptions
                  value={supplierOpt}
                  onChange={(opt: any) => {
                    setSupplierOpt(opt || null);
                    setSel({}); // catalog is supplier-specific
                  }}
                  isClearable
                  placeholder='Search quotation suppliers…'
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                  styles={{ menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }) }}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize='sm'>Notes (applied to every quote)</FormLabel>
                <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder='e.g. delivers daily' />
              </FormControl>
            </Grid>
            {supplierOpt?.raw && <SupplierTermsStrip profile={supplierOpt.raw} />}
          </DrawerHeader>
          <DrawerBody px='4' py='4'>
            {supplierOpt && visibleItems.length === 0 && (
              <Box border='1px dashed' borderColor='gray.300' rounded='md' p='4' fontSize='sm' color='gray.600'>
                Every item in this category is closed (awarded or unfulfilled) — there is nothing left to quote.
              </Box>
            )}
            {visibleItems.length > 2 && (
              <Flex justify='flex-end' gap='3' mb='2'>
                <Button size='xs' variant='link' onClick={() => setAllCollapsed(true)}>
                  Collapse all
                </Button>
                <Button size='xs' variant='link' onClick={() => setAllCollapsed(false)}>
                  Expand all
                </Button>
              </Flex>
            )}
            {visibleItems.map((it) => {
              const s = sel[it.id];
              const quoted = quotedByItem.get(it.id);
              const isCollapsed = !!collapsed[it.id];
              const headerMeta = [
                it.masterProduct?.brand,
                it.volume ? `${it.volume} ${it.masterProduct?.baseUnit || ''}`.trim() : null,
              ]
                .filter(Boolean)
                .join(' · ');
              return (
                <Card key={it.id} mb='3' variant='outline'>
                  <CardBody py='2'>
                    {/* Accordion header — always visible, click toggles the body.
                      The ✓ summary keeps collapsed cards informative. */}
                    <Flex align='center' gap='2' cursor='pointer' py='1' onClick={() => toggleCollapsed(it.id)}>
                      <Box
                        as={FiChevronDown}
                        color='gray.500'
                        flexShrink={0}
                        transform={isCollapsed ? 'rotate(-90deg)' : undefined}
                        transition='transform 0.15s'
                      />
                      <Text fontWeight='semibold' noOfLines={1}>
                        {it.masterProduct?.name || `Item #${it.id}`}
                      </Text>
                      {headerMeta && (
                        <Text fontSize='xs' color='gray.500' noOfLines={1}>
                          {headerMeta}
                        </Text>
                      )}
                      <Box ml='auto' flexShrink={0}>
                        {s ? (
                          <Badge
                            colorScheme={Number(s.price) > 0 ? 'green' : 'orange'}
                            variant='subtle'
                            maxW='280px'
                            overflow='hidden'
                            textOverflow='ellipsis'
                            whiteSpace='nowrap'>
                            ✓{' '}
                            {s.pendingCreate
                              ? `New: ${it.masterProduct?.name || `Item #${it.id}`}${
                                  s.variantLabel ? ` (${s.variantLabel})` : ''
                                }`
                              : s.product?.nameEn || `Product #${s.product?.id}`}
                            {Number(s.price) > 0 ? ` · ${s.price} AED` : ' · price needed'}
                          </Badge>
                        ) : (
                          <Badge colorScheme='gray' variant='subtle'>
                            not selected
                          </Badge>
                        )}
                      </Box>
                    </Flex>
                    <Collapse in={!isCollapsed} animateOpacity>
                      {quoted && (
                        <Box
                          bg='blue.50'
                          border='1px solid'
                          borderColor='blue.200'
                          rounded='md'
                          px='2.5'
                          py='1.5'
                          mt='1'
                          fontSize='xs'
                          color='gray.700'>
                          Already quoted by <b>{supplierOpt?.label}</b>:{' '}
                          {quoted.quotes
                            .map((q) => `${q.product?.nameEn || `SKU #${q.productId}`} · ${q.price} ${q.currency}`)
                            .join(' — ')}
                          . Picking below adds an alternative offer from the same supplier.
                        </Box>
                      )}
                      <Grid templateColumns={{ base: '1fr', lg: '340px 1fr' }} gap='5' pt='2'>
                        <Box borderRight={{ lg: '1px solid' }} borderColor={{ lg: 'gray.100' }} pr={{ lg: '4' }}>
                          <RequestedProductPanel
                            item={it}
                            onEditItem={() => setEditItem(it)}
                            onEditMaster={() => setEditMaster(it)}
                          />
                        </Box>
                        <Box>
                          <ProductMatcher
                            supplierId={supplierOpt?.value}
                            supplierName={supplierOpt?.label}
                            item={it}
                            value={s?.product ?? null}
                            pendingCreate={s?.pendingCreate}
                            pendingVariantLabel={s?.variantLabel}
                            onChange={(p) => pick(it.id, p)}
                            onStageCreate={(variant) => stageCreate(it.id, variant)}
                            quotedProductIds={quotedByItem.get(it.id)?.ids}
                          />
                          {/* Both paths price through the same panel: the
                              supplier's standing price is the starting point
                              whether the line is a catalog product or a
                              staged variant. */}
                          {s && (s.product || s.pendingCreate) && (
                            <Box mt='3'>
                              <QuoteLinePricing
                                supplierId={supplierOpt?.value}
                                familyId={it.masterProduct?.familyId}
                                masterProductId={masterIdFor(it, s)}
                                ladder={ladderFor(it, s)}
                                product={s.product}
                                price={s.price}
                                priceUnit={unitFor(it, s)}
                                sync={s.sync}
                                targetPrice={it.expectedPrice}
                                onPriceChange={(v) => setPrice(it.id, v)}
                                onPriceUnitChange={(tier) => setPriceUnit(it.id, tier)}
                                onSyncChange={(next) => setSync(it.id, next)}
                                onSupplierPrice={(price, tier) => applySupplierPrice(it.id, price, tier)}
                                addDeal={
                                  s.product && supplierCountryCode ? (
                                    <InlineAddDealPopover
                                      product={s.product}
                                      countryCode={supplierCountryCode}
                                      defaultDealPrice={
                                        s.price ||
                                        (s.product.defaultPrice != null ? String(s.product.defaultPrice) : '')
                                      }
                                      onCreated={(deal) => applyDealToSelection(it.id, deal)}
                                    />
                                  ) : undefined
                                }
                                extra={
                                  s.product ? (
                                    <ProductQuoteHistoryModal
                                      productId={s.product.id}
                                      productName={s.product.nameEn || `Product #${s.product.id}`}
                                      excludeInquiryId={excludeInquiryId}
                                      trigger={
                                        <IconButton
                                          aria-label='Quote history'
                                          title='Quote history'
                                          size='sm'
                                          variant='ghost'
                                          icon={<FiClock />}
                                        />
                                      }
                                    />
                                  ) : undefined
                                }
                              />
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Collapse>
                  </CardBody>
                </Card>
              );
            })}
          </DrawerBody>
          <DrawerFooter borderTop='1px solid' borderColor='gray.100' gap='2'>
            <Badge colorScheme={lines.length ? 'green' : 'gray'} rounded='full' px='2' mr='auto'>
              {lines.length} of {visibleItems.length} items selected
            </Badge>
            <Button variant='outline' onClick={onClose} isDisabled={createQuote.isLoading}>
              Cancel
            </Button>
            <Button
              colorScheme='red'
              onClick={save}
              isLoading={createQuote.isLoading || createFromMaster.isLoading}
              loadingText='Saving…'
              isDisabled={!lines.length}>
              Save {lines.length || ''} quote{lines.length === 1 ? '' : 's'}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Inline data-fix loop — both mount over the drawer (later portal wins). */}
      {editItem && (
        <EditItemModal isOpen={!!editItem} onClose={() => setEditItem(null)} inquiryId={inquiryId} item={editItem} />
      )}
      {editMaster && (
        <EditMasterProductModal
          item={editMaster}
          onClose={() => {
            setEditMaster(null);
            // masterProduct is embedded in the inquiry payload — refetch it so
            // the request panel and suggestion ranking pick up the fix. Fires on
            // cancel too; one cheap refetch beats plumbing a save signal out of
            // the shared VariantFormModal.
            queryClient.invalidateQueries([restaurantInquiriesEndpoint]);
          }}
        />
      )}
    </>
  );
};
